import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Navigate, Route, Routes, useLocation } from 'react-router';

import { connect } from 'react-redux';
import { useAppSelector } from 'dux/app/hooks';

import {
  accessoriesProductsRoute,
  commonProductsRoutePath,
  haircareProductsRoute,
  productsSlugs,
  skincareProductsRoute,
} from 'constants/products';

import LoadingScene from 'Scenes/LoadingScene';
import NotFound from 'Scenes/NotFound';

import * as trackingActions from 'dux/tracking/actions';
import { isFlagsDoneFetching, shouldShowScalpSerumContent } from 'dux/featureFlags/selectors';

import { AccessoriesProductCollection } from './accessories/AccessoriesProductCollection';
import AccessoriesProductDetails from './accessories/AccessoriesProductDetails';
import BrushProductDetails from './accessories/BrushProductDetails';
import CandleProductDetails from './accessories/CandleProductDetails';
import HaircareProductCollection from './haircare/HaircareProductCollection';
import HairProductDetails from './haircare/HairProductDetails';
import ProductDetailsSupplements from './haircare/ProductDetailsSupplements';
import SkincareProductCollection from './skincare/SkincareProductCollection';
import SkinProductDetails from './skincare/SkinProductDetails';
import { ProductPage } from './ProductPage';

const formatPath = url => url.split(commonProductsRoutePath)[1];

// Haircare Routes
const haircareRoutes = [
  {
    path: formatPath(haircareProductsRoute[productsSlugs.HAIR_MASK]),
    element: <HairProductDetails productSlug={productsSlugs.HAIR_MASK} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.SCALP_MASK]),
    element: <HairProductDetails productSlug={productsSlugs.SCALP_MASK} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.SHAMPOO]),
    element: <HairProductDetails productSlug={productsSlugs.SHAMPOO} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.CONDITIONER]),
    element: <HairProductDetails productSlug={productsSlugs.CONDITIONER} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.OIL]),
    element: <HairProductDetails productSlug={productsSlugs.OIL} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.DRY_SHAMPOO]),
    element: <HairProductDetails productSlug={productsSlugs.DRY_SHAMPOO} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.CURL_CREAM]),
    element: <HairProductDetails productSlug={productsSlugs.CURL_CREAM} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.LEAVEIN_CONDITIONER]),
    element: <HairProductDetails productSlug={productsSlugs.LEAVEIN_CONDITIONER} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.STYLING_GEL]),
    element: <HairProductDetails productSlug={productsSlugs.STYLING_GEL} />,
  },
  {
    path: formatPath(haircareProductsRoute[productsSlugs.SUPPLEMENT_CORE]),
    element: <ProductDetailsSupplements />,
  },
  { path: 'haircare', element: <HaircareProductCollection /> },
];

// Skincare Routes
const skincareProductsRoutes = [
  {
    path: formatPath(skincareProductsRoute[productsSlugs.CLEANSER]),
    element: <SkinProductDetails productSlug={productsSlugs.CLEANSER} />,
  },
  {
    path: formatPath(skincareProductsRoute[productsSlugs.SERUM]),
    element: <SkinProductDetails productSlug={productsSlugs.SERUM} />,
  },
  {
    path: formatPath(skincareProductsRoute[productsSlugs.MOISTURIZER]),
    element: <SkinProductDetails productSlug={productsSlugs.MOISTURIZER} />,
  },
  {
    path: 'skincare',
    element: <SkincareProductCollection />,
    index: true,
  },
];

// Accessories Routes
const accessoriesProductsRoutes = [
  {
    path: formatPath(accessoriesProductsRoute[productsSlugs.BRUSH]),
    element: <BrushProductDetails />,
  },
  {
    path: formatPath(accessoriesProductsRoute[productsSlugs.CANDLE]),
    element: <CandleProductDetails />,
  },
  {
    path: formatPath(accessoriesProductsRoute[productsSlugs.HAIR_TOWEL_WRAP]),
    element: <AccessoriesProductDetails productSlug={productsSlugs.HAIR_TOWEL_WRAP} />,
  },
  { path: 'accessories', element: <AccessoriesProductCollection /> },
];

export const ProductsRoutes = ({ trackBreadcrumb }) => {
  const { pathname, search } = useLocation();
  const showScalpSerumContent = useAppSelector(shouldShowScalpSerumContent);
  const flagsDoneFetching = useAppSelector(isFlagsDoneFetching);

  useEffect(() => {
    trackBreadcrumb(`${pathname}${search}`);
  }, [pathname, search]);

  return (
    <Routes>
      {/* Accessories Routes */}
      {accessoriesProductsRoutes.map(({ path, element }) => (
        <Route key={path} element={element} path={path} />
      ))}

      {/* Haircare Routes */}
      {haircareRoutes.map(({ path, element }) => (
        <Route key={path} element={element} path={path} />
      ))}

      <Route
        element={
          !flagsDoneFetching ? (
            <LoadingScene />
          ) : showScalpSerumContent ? (
            <ProductPage productSlug={productsSlugs.SCALP_SERUM} />
          ) : (
            <NotFound />
          )
        }
        path={formatPath(haircareProductsRoute[productsSlugs.SCALP_SERUM])}
      />

      {/* Skincare Routes */}
      {skincareProductsRoutes.map(({ path, element }) => (
        <Route key={path} element={element} path={path} />
      ))}

      <Route element={<Navigate replace to="/products/haircare" />} path="*" />
    </Routes>
  );
};

ProductsRoutes.propTypes = {
  trackBreadcrumb: PropTypes.func,
};

ProductsRoutes.defaultProps = {
  trackBreadcrumb: () => {},
};

const ConnectedProductsRoutes = connect(() => ({}), {
  trackBreadcrumb: trackingActions.trackBreadcrumb,
})(ProductsRoutes);

export default ConnectedProductsRoutes;
